/**
 * Modal — overlay panel with header (icon + title + close), body, and
 * optional footer-actions row.
 *
 * Built entirely on the **light-context** color tokens (`inverse-*`)
 * because modals live as overlays on top of the dark base — a designer
 * convention documented in `tokens/inventory/semantics.md`. Panel
 * background is `surface.inverse-surface`, body text is
 * `surface.inverse-on-surface`. The header carries a variant-tinted
 * fill (`{variant}.inverse-{variant}-container`) plus matching text
 * (`{variant}.inverse-on-{variant}-container`).
 *
 * Geometry resolved from Figma:
 *   - Section "Modals" — node 3985:32969
 *   - Primary  — node 3985:32856
 *   - Error    — node 3985:32936
 *   - Success  — node 3985:32945
 *   - Alert    — node 3985:32954
 *
 * Base size: 400 wide, body grows with content.
 * Header: min-height 48px, content padding 16px horizontal / 8px vertical,
 *         gap 4px between leading icon and title.
 * Title: heading.m (18px / 22px Regular).
 * Body: 16px padding all around, body.l text (16px / 20px).
 * Close button: 14px padding, rendered icon at 20px in the variant's
 *               main tone (e.g. `primary.primary`).
 *
 * @format
 */

import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Icon} from 'ui/components/atoms/Icon';
import {Button} from 'ui/components/atoms/Button';
import {text} from 'ui/theme/typography';
import {SurfaceContextProvider, surfaceVar} from 'ui/theme/surfaceContext';

import bordersTokens from 'ui/tokens/semantic/borders.json';
import {v} from 'ui/tokens/util';

const RADIUS_M_PX = parseInt(v(bordersTokens.radius.m));

export const MODAL_VARIANTS = ['primary', 'error', 'success', 'alert'] as const;
export type ModalVariant = (typeof MODAL_VARIANTS)[number];

export interface ModalProps {
  /** Tone of the header tint + close button. Default `primary`. */
  variant?: ModalVariant;

  /** Title text rendered next to the leading icon. */
  title: string;

  /**
   * Override the leading icon. Material Symbols ligature. When omitted,
   * the variant default is used (see DEFAULT_ICON below).
   */
  icon?: string;

  /** Modal body content. */
  children: ReactNode;

  /**
   * Optional footer with action buttons. Rendered below the body in a
   * border-top section. Drop Button atoms inside.
   */
  footerActions?: ReactNode;

  /**
   * Close handler — fires when the user clicks × or (with `backdrop`
   * enabled) clicks the backdrop. Omit to hide the × button entirely.
   */
  onClose?: () => void;

  /**
   * Wrap the panel in a dark backdrop overlay. Default `false`. Set to
   * `true` for production page-modal use; leave off in Storybook stories
   * where the modal is the only thing on screen.
   */
  backdrop?: boolean;

  /** Fixed panel width. Default 400px (matches Figma). */
  width?: number | string;
}

// ── Variant → defaults ─────────────────────────────────────────────────

const DEFAULT_ICON: Record<ModalVariant, string> = {
  primary: 'info',
  error: 'error',
  success: 'check_circle',
  alert: 'warning',
};

/**
 * Resolve the header tint + matching on-tint text colour for a given
 * Modal variant. Reads from `theme.palette.{primary,bad,good,alert}.
 * inverseContainer / inverseOnContainer` — light-context container
 * pair shipped with the role.
 */
// Brand role driving the header tint per Modal variant.
const HEADER_ROLE: Record<ModalVariant, 'primary' | 'bad' | 'good' | 'alert'> = {
  primary: 'primary',
  error: 'bad',
  success: 'good',
  alert: 'alert',
};

function headerTone(variant: ModalVariant): {fill: string; onFill: string} {
  // Return CSS-var REFERENCES, not `theme.palette.X.container` literals.
  // Modal wraps its content in `<SurfaceContextProvider value="light">`
  // below, which flips every `--ads-palette-*` var on the subtree to the
  // LIGHT scheme. Reading `theme.palette.primary.container` in a styled
  // callback bakes the DARK literal and ignores that cascade (custom
  // `container`/`onContainer` keys are not var-ified by MUI — same class
  // as surfaceVar; see theme/surfaceContext.tsx). Referencing the var
  // directly lets the light cascade take effect → the header renders in
  // its light-scheme tint.
  const role = HEADER_ROLE[variant];
  return {
    fill: `var(--ads-palette-${role}-container)`,
    onFill: `var(--ads-palette-${role}-onContainer)`,
  };
}

// Modal `variant` names (primary / error / success / alert) describe
// SEMANTIC INTENT — what the modal is about (a confirmation, an error
// report, a success acknowledgement). Button `color` describes PALETTE
// ROLE (which brand spectrum to render in). The two map deterministically
// but the vocabularies stay distinct intentionally — a "success" modal
// uses the `good` palette role, an "error" modal uses `bad`. The mapping
// lives at the close-button call site below; resist the urge to extract
// it back into a lookup table, the table was previously a symptom of
// mismatched docs, not a useful abstraction.

// ── Chrome ─────────────────────────────────────────────────────────────

const Backdrop = styled(Box)({
  position: 'fixed',
  inset: 0,
  // 60% black overlay — readable on both light and dark backgrounds.
  // TODO: lift into `tokens/semantic/color.json` as `overlay.scrim` once
  // the design-system semantic layer adopts a dedicated overlay group;
  // until then this stays as a single-source literal here (the only
  // scrim in the system).
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Modals sit above almost everything else. The `theme.zIndex.modal`
  // base in MUI is 1300; we use 1000 because backdrops on our Pages
  // (which currently use ad-hoc z-indexes like 1 for headers) only need
  // to clear those — going higher would clip the storybook chrome on
  // local previews.
  zIndex: 1000,
});

const Panel = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$width: number | string}>(({$width}) => ({
  width: typeof $width === 'number' ? `${$width}px` : $width,
  display: 'flex',
  flexDirection: 'column',
  // Modal is intrinsically light — the SurfaceContextProvider wrapper
  // below sets the subtree scheme to 'light'. Reference the CSS vars
  // directly (not `theme.palette.*`, which bakes the dark literal and
  // silently ignores the cascade — see surfaceVar / surfaceContext.tsx).
  backgroundColor: 'var(--ads-palette-background-default)',
  color: surfaceVar('onSurface'),
  borderRadius: RADIUS_M_PX,
  overflow: 'hidden', // clip the tinted header to the panel radius
}));

const Header = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$fill: string; $onFill: string}>(({theme, $fill, $onFill}) => ({
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  backgroundColor: $fill,
  color: $onFill,
  gap: theme.spacing(4),
}));

const HeaderTitleBlock = styled(Box)(({theme}) => ({
  flex: '1 1 auto',
  minHeight: '48px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2, 4),
  ...text('heading.m'),
}));

// CloseButton lives directly in JSX as a `<Button variant="ghost"
// color={...}>` — see the Component body. No custom styled component
// is needed because the system Button already covers the geometry
// (icon-only size=m → 48×48), the brand colour (via variant mapping),
// the light-context spectrum (via Modal's SurfaceContextProvider) and
// the systemic ghost hover (opacity-based, works on tinted headers).

const Body = styled(Box)(({theme}) => ({
  padding: theme.spacing(4),
  ...text('body.l'),
  color: surfaceVar('onSurface'),
}));

const Footer = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  // var reference, not theme.palette.disabledStates.low (custom namespace →
  // bakes dark literal, ignores the light cascade).
  borderTop: '1px solid var(--ads-palette-disabledStates-low)',
}));

// ── Component ──────────────────────────────────────────────────────────

// Inline variant→Button-color mapping. See the rationale comment above.
function buttonColorForVariant(variant: ModalVariant): 'primary' | 'good' | 'bad' | 'alert' {
  switch (variant) {
    case 'primary':
      return 'primary';
    case 'error':
      return 'bad';
    case 'success':
      return 'good';
    case 'alert':
      return 'alert';
  }
}

export function Modal({
  variant = 'primary',
  title,
  icon,
  children,
  footerActions,
  onClose,
  backdrop = false,
  width = 400,
}: ModalProps) {
  const iconName = icon ?? DEFAULT_ICON[variant];
  const tone = headerTone(variant);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // ── Body scroll lock ───────────────────────────────────────────────
  // Only meaningful when the modal is opened over a backdrop (without
  // the backdrop the surrounding page intentionally stays interactive,
  // e.g. when a modal demos as the only thing in a story canvas).
  useEffect(() => {
    if (!backdrop) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [backdrop]);

  // ── Auto-focus the panel on mount ──────────────────────────────────
  // Sends the keyboard into the modal so Esc / Tab work without the
  // user clicking first.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  // ── Esc closes the modal ───────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape' && onClose) {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose],
  );

  // ── Focus trap ─────────────────────────────────────────────────────
  // When Tab walks past the last focusable, loop back to the first;
  // Shift+Tab past the first loops to the last. Keeps keyboard users
  // inside the modal as long as it's open.
  const handleTab = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusables = panelRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const panel = (
    // Light context propagates to every descendant — Buttons / Inputs /
    // Links inside the modal automatically resolve their tokens against
    // the `inverse-*` spectrum without per-call wiring.
    <SurfaceContextProvider value='light'>
      <Panel
        ref={panelRef}
        $width={width}
        role='dialog'
        aria-modal='true'
        aria-label={title}
        tabIndex={-1}
        onKeyDown={e => {
          handleKeyDown(e);
          handleTab(e);
        }}
      >
        <Header $fill={tone.fill} $onFill={tone.onFill}>
          <HeaderTitleBlock>
            {/* Variant icon — 22px to match the title's line height; uses
                `inherit` color so it picks up the on-container tone. */}
            <Icon name={iconName} size={22} color='inherit' />
            <Box component='span'>{title}</Box>
          </HeaderTitleBlock>
          {onClose && (
            // Close button is a system Button atom — ghost variant
            // with the variant-mapped colour. Light-context propagates
            // from the SurfaceContextProvider above, so the brand
            // colour resolves to the right `inverse-*` token. Icon
            // size 20 + Button size m → 48×48 square, matching Figma.
            <Button
              variant='ghost'
              color={buttonColorForVariant(variant)}
              size='m'
              type='button'
              aria-label='Close'
              onClick={onClose}
              leftIcon={<Icon name='close' size={20} color='inherit' />}
            />
          )}
        </Header>
        <Body>{children}</Body>
        {footerActions && <Footer>{footerActions}</Footer>}
      </Panel>
    </SurfaceContextProvider>
  );

  return backdrop ? <Backdrop onClick={handleBackdropClick}>{panel}</Backdrop> : panel;
}
